import { ReferenceRangeDisplay } from "@/components/ReferenceRangeDisplay/ReferenceRangeDisplay";
import { BodyText, Callout, toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { EditableRecord } from "@/features/Patient/MedicalRecords/components/EditableRecord";
import { formatDate, formatUtcDateTime } from "@/lib/time";
import { trackEvent } from "@/lib/tracking";
import { useParams } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import type { z } from "zod";
import type {
  VitalDataMap,
  VitalFormData,
  VitalParamsMap,
  VitalsType,
} from "../config/vitalsTypes";
import {
  useCreateVitalsObservation,
  useVitalsObservation,
} from "../network/useVitalsObservation";
import { VitalForm } from "./VitalForm";
import { VitalHistoryModal } from "./VitalHistoryModal";
import { VitalSkeleton } from "./VitalSkeleton";

const EDIT_TRACKER_MAP: Record<VitalsType, string> = {
  "blood-pressure": TRACKERS.EDIT_BP,
  "heart-rate": TRACKERS.EDIT_HEART_RATE,
  height: TRACKERS.EDIT_HEIGHT,
  weight: TRACKERS.EDIT_WEIGHT,
  "body-temperature": TRACKERS.EDIT_TEMPERATURE,
  "head-circumference": "",
};

const CANCEL_TRACKER_MAP: Record<VitalsType, string> = {
  "blood-pressure": TRACKERS.CANCEL_BP,
  "heart-rate": TRACKERS.CANCEL_HEART_RATE,
  height: TRACKERS.CANCEL_HEIGHT,
  weight: TRACKERS.CANCEL_WEIGHT,
  "body-temperature": TRACKERS.CANCEL_TEMPERATURE,
  "head-circumference": "",
};

const SAVE_TRACKER_MAP: Record<VitalsType, string> = {
  "blood-pressure": TRACKERS.SAVE_BP,
  "heart-rate": TRACKERS.SAVE_HEART_RATE,
  height: TRACKERS.SAVE_HEIGHT,
  weight: TRACKERS.SAVE_WEIGHT,
  "body-temperature": TRACKERS.SAVE_TEMPERATURE,
  "head-circumference": "",
};

export interface VitalItemProps<T extends VitalsType> {
  type: T;
  title: string;
  formSchema: z.ZodSchema<VitalFormData>;
  formDescription: string;
  format: (data: VitalDataMap[T], fallback: string) => string | ReactNode;
  getFormValue?: (data: VitalDataMap[T]) => string;
  createMutationPayload: (
    patientId: string,
    formValue: string,
    reportedDate: string,
  ) => VitalParamsMap[T];
}

export const VitalItem = <T extends VitalsType>({
  type,
  title,
  formSchema,
  formDescription,
  format,
  getFormValue,
  createMutationPayload,
}: VitalItemProps<T>) => {
  const { patientId } = useParams({ strict: false });
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const {
    data: vitalsData,
    isLoading,
    isError,
    refetch,
  } = useVitalsObservation(type, patientId);

  const { mutateAsync: createVitalObservation, isPending } =
    useCreateVitalsObservation(type, patientId as string);

  const handleSubmit = async (values: VitalFormData) => {
    if (!patientId) return;

    // Track the save action based on vital type
    const trackerId = SAVE_TRACKER_MAP[type];
    if (trackerId) {
      trackEvent(trackerId);
    }

    try {
      // Set the date to noon in its current timezone before formatting
      const noonDate = new Date(values.effectiveDateTime);
      noonDate.setHours(12, 0, 0, 0);

      const mutationData = createMutationPayload(
        patientId,
        values.value,
        formatUtcDateTime(noonDate.toISOString()),
      );

      await createVitalObservation(mutationData);
      setEditing(false);
    } catch (error) {
      toast.error(`Failed to create ${type} observation.`, {
        id: `${type}-error`,
      });
    }
  };

  const handleCancel = () => {
    // Track the cancel action based on vital type
    const trackerId = CANCEL_TRACKER_MAP[type];
    if (trackerId) {
      trackEvent(trackerId);
    }

    setEditing(false);
  };
  const handleEditClick = () => {
    // Track the edit action based on vital type
    const trackerId = EDIT_TRACKER_MAP[type];
    if (trackerId) {
      trackEvent(trackerId);
    }

    setEditing(true);
  };

  if (isLoading || isPending) return <VitalSkeleton title={title} />;

  if (isError || !vitalsData) {
    return (
      <div className="flex flex-col gap-2 py-2 w-full">
        <Callout
          className="w-full cursor-pointer"
          variant="danger"
          title={`Unable to load ${title}`}
          description="Click to try again."
          onClick={() => refetch()}
        />
      </div>
    );
  }

  const displayValue = format(
    vitalsData,
    vitalsData.effectiveDateTime ? "Invalid data" : "Unknown",
  );
  const formValue = getFormValue
    ? getFormValue(vitalsData)
    : format(vitalsData, "");

  // Convert displayValue to string for ReferenceRangeDisplay
  // Most format functions return strings, String() handles other cases
  const displayValueString =
    typeof displayValue === "string" ? displayValue : String(displayValue);

  return (
    <>
      <EditableRecord
        displayComponent={
          <>
            <BodyText size="sm">
              <ReferenceRangeDisplay
                criticality={vitalsData.criticality ?? null}
                data={displayValueString}
                referenceRanges={vitalsData.referenceRanges ?? null}
                tooltipFallback=""
              />
            </BodyText>{" "}
            <BodyText size="xs" textColor="secondary">
              {vitalsData.effectiveDateTime
                ? formatDate(vitalsData.effectiveDateTime)
                : ""}
            </BodyText>
          </>
        }
        formComponent={
          <VitalForm
            type={type}
            title={title}
            description={formDescription}
            schema={formSchema}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isPending}
            defaultValues={{
              value: typeof formValue === "string" ? formValue : "",
              effectiveDateTime: new Date().toISOString(),
            }}
          />
        }
        isEditing={editing}
        onEdit={handleEditClick}
        title={title}
        onViewHistory={() => setShowHistory(true)}
      />
      <VitalHistoryModal
        open={showHistory}
        onOpenChange={setShowHistory}
        vitalsType={type}
        patientId={patientId}
        format={format}
      />
    </>
  );
};
